/**
 * ORGU-284: the owner's opt-in to the remote access relay. Off by default; turning it on asks
 * first, turning it off acts at once (the link closes, remote browsers lose access). Both are
 * audited by the hub. Errors show inline, never as a toast.
 */
import { useState } from 'react';
import type { TrustState } from '@orgus/protocol';
import { ConfirmDialog, ErrorState, Panel, RelativeTime, StatusDot, Switch } from '@orgus/ui';
import { Globe } from '@orgus/ui/icons';
import { useCan } from '../auth/useMe.ts';
import type { TrustApi } from './useTrust.ts';

const LINK_LABEL = { off: 'Off', unconfigured: 'No relay address', connecting: 'Connecting', connected: 'Connected' } as const;
const LINK_TONE = { off: 'idle', unconfigured: 'waiting', connecting: 'waiting', connected: 'passed' } as const;

export function RelayAccess({ t, s }: { t: TrustApi; s: TrustState }) {
  const isOwner = useCan('owner');
  const [confirmOn, setConfirmOn] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const relay = s.relay;
  if (!relay) return null;
  const set = async (on: boolean) => {
    setError(null);
    try {
      await t.saveSettings({ relay: on });
    } catch (e) {
      setError(e);
    }
  };
  return (
    <Panel title="Remote access" icon={<Globe />} sub={LINK_LABEL[relay.link]}>
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <label htmlFor="trust-relay" className="text-sm font-medium text-fg">
              Reach this hub from anywhere
            </label>
            <p id="trust-relay-hint" className="text-sm text-fg-muted">
              {relay.on ? 'Off closes the link at once.' : 'Off. Nothing leaves this computer.'}
              {!isOwner && ' Only the owner can change it.'}
            </p>
          </div>
          <Switch id="trust-relay" aria-describedby="trust-relay-hint" checked={relay.on} disabled={!isOwner} onCheckedChange={(on) => (on ? setConfirmOn(true) : void set(false))} />
        </div>
        {relay.on && (
          <div className="flex items-center gap-2 text-caption text-fg-subtle">
            <StatusDot tone={LINK_TONE[relay.link]} />
            <span>{LINK_LABEL[relay.link]}</span>
            {relay.since !== null && (
              <span>
                since <RelativeTime ts={relay.since} />
              </span>
            )}
            {relay.error && relay.link !== 'connected' && <span className="truncate">{relay.error}</span>}
          </div>
        )}
        {error != null && <ErrorState variant="inline" error={error} />}
      </div>
      <ConfirmDialog
        open={confirmOn}
        onOpenChange={setConfirmOn}
        title="Turn on remote access?"
        description="Turn it off here to close the link at once."
        confirmLabel="Turn on"
        tone="primary"
        onConfirm={() => set(true)}
      />
    </Panel>
  );
}
